import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, MessageSquare, ArrowLeft } from 'lucide-react';
import FeedCard from '@/components/feed/FeedCard';

function CommunityCard({ community, i }) {
  const [joined, setJoined] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`community-card-${i}`}>
      <img src={community.coverImage} alt="" className="w-full h-32 object-cover" />
      <div className="p-4">
        <Link to={`/communities/${community.slug}`} className="font-semibold text-slate-900 hover:text-[#7C3AED] transition-colors">{community.name}</Link>
        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{community.description}</p>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <div className="flex">
              {community.memberAvatars?.slice(0, 3).map((a, j) => <img key={j} src={a} alt="" className="w-5 h-5 rounded-full border-2 border-white -ml-1 first:ml-0" />)}
            </div>
            <span className="text-xs text-slate-400">{community.memberCount} members</span>
          </div>
          <button onClick={() => setJoined(!joined)} className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${joined ? 'bg-[#7C3AED] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`join-${community.slug}`}>
            {joined ? 'Joined' : 'Join'}
          </button>
        </div>
        <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
          <MessageSquare className="w-3 h-3" /> {community.postsThisWeek} posts this week
        </div>
      </div>
    </motion.div>
  );
}

export function CommunitiesListPage() {
  const [communities, setCommunities] = useState([]);
  useEffect(() => { fetch('/data/communities.json').then(r => r.json()).then(setCommunities).catch(() => {}); }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="communities-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Communities</h1>
          <p className="text-sm text-slate-500 mt-1">Join cause-based communities and connect with changemakers.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {communities.map((c, i) => <CommunityCard key={c.id} community={c} i={i} />)}
      </div>
    </div>
  );
}

export function CommunityDetailPage() {
  const { slug } = useParams();
  const [community, setCommunity] = useState(null);
  const [activeTab, setActiveTab] = useState('discussion');

  useEffect(() => {
    const load = async () => {
      try {
        const [communities, feedPosts] = await Promise.all([
          fetch('/data/communities.json').then(r => r.json()),
          fetch('/data/feed_posts.json').then(r => r.json()),
        ]);
        const found = communities.find(c => c.slug === slug);
        if (!found) {
          setCommunity({ error: 'Community not found' });
          return;
        }
        const matchKey = (found.causeArea || '').toLowerCase();
        const matchedPosts = feedPosts.filter(p => ((p.content || '') + (p.storyTitle || '')).toLowerCase().includes(matchKey)).slice(0, 10);
        setCommunity({ ...found, posts: matchedPosts.length > 0 ? matchedPosts : feedPosts.slice(0, 5) });
      } catch {
        setCommunity({ error: 'Community not found' });
      }
    };
    load();
  }, [slug]);

  if (!community) return <div className="flex items-center justify-center h-64 text-slate-400 text-sm">Loading...</div>;
  if (community.error) return <div className="flex items-center justify-center h-64 text-slate-400 text-sm">Community not found</div>;

  const tabs = ['Discussion', 'Projects', 'Members'];

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="community-detail-page">
      <Link to="/communities" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"><ArrowLeft className="w-4 h-4" /> Back to Communities</Link>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <img src={community.coverImage} alt="" className="w-full h-40 sm:h-52 object-cover" />
        <div className="p-5">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">{community.name}</h1>
              <p className="text-sm text-slate-500 mt-1">{community.description}</p>
              <div className="flex items-center gap-3 mt-3">
                <div className="flex items-center gap-1.5 text-sm text-slate-500"><Users className="w-4 h-4" /> {community.memberCount} members</div>
                <div className="flex items-center gap-1.5 text-sm text-slate-500"><MessageSquare className="w-4 h-4" /> {community.postsThisWeek} posts/week</div>
              </div>
            </div>
            <button className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white text-sm font-semibold" data-testid="join-community-btn">Join Community</button>
          </div>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-slate-200">
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t.toLowerCase())} className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === t.toLowerCase() ? 'text-[#7C3AED] border-[#7C3AED]' : 'text-slate-500 border-transparent hover:text-slate-700'}`} data-testid={`tab-${t.toLowerCase()}`}>
            {t}
          </button>
        ))}
      </div>

      {activeTab === 'discussion' && (
        <div className="space-y-4">
          {(community.posts || []).map((post, i) => <FeedCard key={post.id} post={post} index={i} />)}
          {(!community.posts || community.posts.length === 0) && <p className="text-center py-8 text-slate-400 text-sm">No posts yet in this community.</p>}
        </div>
      )}
      {activeTab === 'members' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {community.memberAvatars?.map((a, i) => (
            <div key={i} className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-3" data-testid={`member-${i}`}>
              <img src={a} alt="" className="w-8 h-8 rounded-full" />
              <span className="text-sm text-slate-700 font-medium">Member {i + 1}</span>
            </div>
          ))}
        </div>
      )}
      {activeTab === 'projects' && <p className="text-center py-8 text-slate-400 text-sm">Community projects coming soon.</p>}
    </div>
  );
}
